/**
 * AdMesh Style Injection System
 * 
 * Provides platform-agnostic, isolated styling for AdMesh components
 * that prevents interference from host platform CSS frameworks
 */

const ADMESH_STYLE_ID = 'admesh-ui-sdk-styles';
const ADMESH_RESET_ID = 'admesh-ui-sdk-reset';

/**
 * CSS Reset for AdMesh components
 * Normalizes styles to prevent host platform CSS from affecting AdMesh components
 */
const ADMESH_CSS_RESET = `
/* AdMesh UI SDK - CSS Reset & Normalization */
.admesh-component,
.admesh-component * {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  border: 0;
  font-size: 100%;
  font-weight: normal;
  font-style: normal;
  line-height: 1.5;
  text-decoration: none;
  background: transparent;
  color: inherit;
}

.admesh-component {
  all: revert;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

.admesh-component button,
.admesh-component a {
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  color: inherit;
}

.admesh-component a {
  text-decoration: none;
  color: inherit;
}

.admesh-component button:focus,
.admesh-component a:focus {
  outline: none;
}

.admesh-component img {
  max-width: 100%;
  height: auto;
  display: block;
}

.admesh-component ul,
.admesh-component ol {
  list-style: none;
}

.admesh-component table {
  border-collapse: collapse;
  border-spacing: 0;
}

.admesh-component input,
.admesh-component textarea,
.admesh-component select {
  font-family: inherit;
  font-size: inherit;
  color: inherit;
}

/* Prevent Tailwind and other frameworks from affecting AdMesh */
.admesh-component .prose,
.admesh-component .container,
.admesh-component .grid,
.admesh-component .flex {
  all: revert;
}

/* Ensure AdMesh components are not affected by dark mode utilities */
.admesh-component.dark,
.admesh-component[data-admesh-theme="dark"] {
  color-scheme: dark;
}

.admesh-component.light,
.admesh-component[data-admesh-theme="light"] {
  color-scheme: light;
}
`;

/**
 * Core AdMesh Component Styles
 * Self-contained styles that work independently of host platform
 */
const ADMESH_CORE_STYLES = `
/* AdMesh Core Component Styles */

.admesh-component {
  position: relative;
  display: block;
}

/* Product Card Styles */
.admesh-product-card {
  position: relative;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  border-radius: 0.75rem;
  background: rgb(255, 255, 255);
  border: 1px solid rgb(229, 231, 235);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.admesh-product-card[data-admesh-theme="dark"] {
  background: rgb(17, 24, 39);
  border-color: rgb(31, 41, 55);
}

.admesh-product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  border-color: rgba(99, 102, 241, 0.2);
}

.admesh-product-card__container {
  position: relative;
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  z-index: 2;
}

.admesh-product-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.admesh-product-card__title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  color: rgb(17, 24, 39);
  letter-spacing: -0.025em;
  transition: all 0.3s ease;
}

.admesh-product-card[data-admesh-theme="dark"] .admesh-product-card__title {
  color: rgb(243, 244, 246);
}

.admesh-product-card:hover .admesh-product-card__title {
  transform: translateX(4px);
}

.admesh-product-card__cta {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0.75rem 1.5rem;
  background: rgb(0, 0, 0);
  color: rgb(255, 255, 255);
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.admesh-product-card[data-admesh-theme="dark"] .admesh-product-card__cta {
  background: rgb(255, 255, 255);
  color: rgb(0, 0, 0);
}

.admesh-product-card__cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.admesh-product-card__cta:active {
  transform: translateY(0);
}

/* Summary Unit Styles */
.admesh-summary-unit {
  font-size: 1rem;
  line-height: 1.6;
  color: rgb(17, 24, 39);
}

.admesh-summary-unit[data-admesh-theme="dark"] {
  color: rgb(243, 244, 246);
}

.admesh-summary-unit a {
  color: rgb(37, 99, 235);
  text-decoration: underline;
  text-decoration-color: rgb(37, 99, 235);
  text-underline-offset: 2px;
  transition: all 0.2s ease;
  font-weight: 500;
}

.admesh-summary-unit[data-admesh-theme="dark"] a {
  color: rgb(96, 165, 250);
  text-decoration-color: rgb(96, 165, 250);
}

.admesh-summary-unit a:hover {
  color: rgb(29, 78, 216);
  text-decoration-color: rgb(29, 78, 216);
}

.admesh-summary-unit[data-admesh-theme="dark"] a:hover {
  color: rgb(147, 197, 253);
  text-decoration-color: rgb(147, 197, 253);
}

/* Ecommerce Cards Styles */
.admesh-ecommerce-container {
  position: relative;
  width: 100%;
}

.admesh-ecommerce-card {
  flex-shrink: 0;
  background: white;
  border: 1px solid rgb(229, 231, 235);
  border-radius: 0.5rem;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  position: relative;
}

.admesh-ecommerce-card[data-admesh-theme="dark"] {
  background: rgb(31, 41, 55);
  border-color: rgb(55, 65, 81);
  color: white;
}

.admesh-ecommerce-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

/* Badge Styles */
.admesh-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  border-radius: 0.5rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.admesh-badge--primary {
  background: linear-gradient(135deg, rgb(99, 102, 241), rgb(79, 70, 229));
  color: white;
  box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.3);
}

.admesh-badge--secondary {
  background: rgb(243, 244, 246);
  color: rgb(55, 65, 81);
}

.admesh-badge[data-admesh-theme="dark"].admesh-badge--secondary {
  background: rgb(31, 41, 55);
  color: rgb(209, 213, 219);
}

/* Responsive */
@media (max-width: 640px) {
  .admesh-product-card__container {
    padding: 0.75rem;
    gap: 0.5rem;
  }

  .admesh-product-card__title {
    font-size: 1.25rem;
  }

  .admesh-product-card__cta {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }
}
`;

/**
 * Inject AdMesh styles into the document
 * Ensures styles are loaded only once and don't conflict with host platform
 */
export const injectAdMeshStyles = (): void => {
  if (typeof document === 'undefined') return;

  // Check if styles already injected
  if (document.getElementById(ADMESH_RESET_ID) && document.getElementById(ADMESH_STYLE_ID)) {
    return;
  }

  // Inject CSS Reset
  if (!document.getElementById(ADMESH_RESET_ID)) {
    const resetStyle = document.createElement('style');
    resetStyle.id = ADMESH_RESET_ID;
    resetStyle.textContent = ADMESH_CSS_RESET;
    document.head.appendChild(resetStyle);
  }

  // Inject Core Styles
  if (!document.getElementById(ADMESH_STYLE_ID)) {
    const coreStyle = document.createElement('style');
    coreStyle.id = ADMESH_STYLE_ID;
    coreStyle.textContent = ADMESH_CORE_STYLES;
    document.head.appendChild(coreStyle);
  }
};

/**
 * Remove AdMesh styles from the document
 * Useful for cleanup or testing
 */
export const removeAdMeshStyles = (): void => {
  if (typeof document === 'undefined') return;

  const resetStyle = document.getElementById(ADMESH_RESET_ID);
  const coreStyle = document.getElementById(ADMESH_STYLE_ID);

  if (resetStyle) resetStyle.remove();
  if (coreStyle) coreStyle.remove();
};

/**
 * Check if AdMesh styles are already injected
 */
export const areAdMeshStylesInjected = (): boolean => {
  if (typeof document === 'undefined') return false;
  return !!(document.getElementById(ADMESH_RESET_ID) && document.getElementById(ADMESH_STYLE_ID));
};

